/* @flow */
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import idx from 'idx';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import * as React from 'react';
import { OrderedMap } from 'immutable';

import Avatar from 'components/Avatar';
import FileEntity from 'entities/File';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import Menu from 'components/Menu';
import AvatarEditorModalTriggerButton from 'components/AvatarEditorModalTriggerButton';
import { uploadRequest, loadThumbnail, removeFile } from 'reducers/fileUploads';
import { showToast, showAPIErrorToast } from 'reducers/toasts';
import { getFirstFile, getFirstFileId } from 'lib/fileUploadHelper';

import type { UploadRequestData } from 'reducers/fileUploads';

import './styles/ProfilePictureUploadButton.scss';

const FILE_CONTEXT_KEY = 'profile_photo';
const PLACEHOLDER_URL = require('assets/images/avatars/profile-picture-placeholder.svg');

type Props = {
  /* graphql props */
  imageUrl?: string,
  updatePhoto: (file: FileEntity) => Promise<*>,
  removePhoto: () => Promise<*>,

  /* redux props */
  showAPIErrorToast: (error: *) => *,
  showToast: (text: string, kind: 'success' | 'error') => *,
  removeFile: (fileContextKey: string, filename: string) => void,
  uploadRequest: (fileContextKey: string, filename: string, blob: File) => void,
  loadThumbnail: (fileContextKey: string, filename: string, thumbnail: string) => void,
  fileUploads: OrderedMap<string, UploadRequestData>,
};

class ProfilePictureUploadButton extends React.Component<Props> {
  removePhotoAndToast = () => {
    const { fileUploads } = this.props;
    const firstFileId = getFirstFileId(fileUploads);
    const removeFirstFile = () =>
      firstFileId && this.props.removeFile(FILE_CONTEXT_KEY, firstFileId);

    this.props.removePhoto().then(
      () => {
        removeFirstFile();
        this.props.showToast('Profile photo removed', 'success');
      },
      e => this.props.showAPIErrorToast(e)
    );
  };

  updatePhotoAndToast = (file: FileEntity) => {
    this.props
      .updatePhoto(file)
      .then(
        () => this.props.showToast('Your profile photo has been added!', 'success'),
        e => this.props.showAPIErrorToast(e)
      );
  };

  renderMenu() {
    return (
      <Menu>
        <MenuItem onClick={this.removePhotoAndToast}>Remove current photo</MenuItem>
      </Menu>
    );
  }

  render() {
    const { imageUrl, fileUploads } = this.props;

    if (imageUrl) {
      return (
        <MenuButton className="ProfilePictureUploadButton" level="icon" menu={this.renderMenu()}>
          <Avatar className="ProfilePictureUploadButton-avatar" image={imageUrl} />
        </MenuButton>
      );
    }

    const uploadRequestData = getFirstFile(fileUploads);
    if (uploadRequestData) {
      return (
        <div className="ProfilePictureUploadButton">
          <Avatar
            className="ProfilePictureUploadButton-thumbnail"
            image={uploadRequestData.thumbnail}
          />
        </div>
      );
    }

    return (
      <div className="ProfilePictureUploadButton">
        <AvatarEditorModalTriggerButton
          className="ProfilePictureUploadButton-uploadButton"
          uploadRequest={this.props.uploadRequest}
          loadThumbnail={this.props.loadThumbnail}
          fileContextKey={FILE_CONTEXT_KEY}
          level="icon"
          onSuccess={this.updatePhotoAndToast}>
          <Avatar className="ProfilePictureUploadButton-avatar" image={PLACEHOLDER_URL} />
        </AvatarEditorModalTriggerButton>
      </div>
    );
  }
}

const withMutations = graphql(
  gql`
    mutation updateUserProfilePicture($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          uuid
          profile_picture {
            urls {
              cdn
            }
          }
        }
        error {
          message
        }
      }
    }
  `,
  {
    props({ mutate }) {
      return {
        updatePhoto(file: FileEntity) {
          const input = { profile_picture_id: file.id };
          return mutate({ variables: { input } });
        },
        removePhoto() {
          const input = { profile_picture_id: null };
          return mutate({ variables: { input } });
        },
      };
    },
  }
);

const withPicture = graphql(
  gql`
    query ProfilePictureUploadButtonQuery {
      user: me {
        uuid
        profile_picture {
          urls {
            cdn
          }
        }
      }
    }
  `,
  {
    props: ({ data }) => {
      const imageUrl = idx(data, _ => _.user.profile_picture.urls.cdn);
      if (!imageUrl) {
        return {};
      }

      return { imageUrl };
    },
  }
);

const mapStateToProps = (state: Object) => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(FILE_CONTEXT_KEY);
  return {
    fileUploads: fileGroup || new OrderedMap(),
  };
};

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators(
    { showToast, showAPIErrorToast, uploadRequest, loadThumbnail, removeFile },
    dispatch
  );
}

export { ProfilePictureUploadButton };
export default compose(
  withMutations,
  withPicture,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePictureUploadButton);
